/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IProductCollectionViewProps,
    IProductComponentViewProps
} from '@msdyn365-commerce-modules/product-collection/src/modules/product-collection/./product-collection';
import { getToggleLang, getUserPreferredLanguage, getUserPrefLang } from '../../../utilities/language-change';
import { HOME_PAGE, KWR, MAORI_LANG } from '../../../utilities/constants';
import { IProductCollectionProps } from '../definition-extensions/product-collection.ext.props.autogenerated';
import * as Msdyn365 from '@msdyn365-commerce/core';

const ProductCollectionView: React.FC<IProductCollectionViewProps & IProductCollectionProps<{}>> = props => {
    const {
        heading,
        ProductCollectionContainer,
        products,
        SingleSlideCarouselComponentProps,
        GridComponentProps,
        isCarousel,
        context
    } = props;
    const { headingInMaori } = props.config;

    // Identifying Language
    const toggleLang = getToggleLang(context);
    const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
    const preferredLanguage: any = getUserPreferredLanguage(KWR, HOME_PAGE, userPrefLang, toggleLang, context);

    const handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (props.config.heading!.text = event.target.value);
    let headingInMaoriLang = headingInMaori && (
        <Msdyn365.Text
            className='ms-product-collection__heading'
            tag={headingInMaori.tag || 'h1'}
            text={headingInMaori ? headingInMaori.text : ''}
            editProps={{ onEdit: handleHeadingChange, requestContext: props.context.request }}
        />
    );
    let titleHeading: any = preferredLanguage === MAORI_LANG ? headingInMaoriLang : heading;
    if (!headingInMaoriLang) {
        titleHeading = heading;
    }

    if (products) {
        return (
            <Module {...ProductCollectionContainer}>
                {titleHeading}
                {isCarousel ? _renderCarousel(SingleSlideCarouselComponentProps, products) : _renderGrid(GridComponentProps, products)}
            </Module>
        );
    }
    props.context.telemetry.error('Product collection content is empty, module wont render.');
    return null;
};

const _renderCarousel = (carouselContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...carouselContainer}>{items && items.map(_renderProduct)}</Node>;
};

const _renderGrid = (gridContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...gridContainer}>{items && items.map(_renderProduct)}</Node>;
};

const _renderProduct = (product: IProductComponentViewProps): JSX.Element => {
    const { ProductContainer, productComponent } = product;

    return <Node {...ProductContainer}>{productComponent}</Node>;
};

export default ProductCollectionView;
